/**
 * One style card in the 2-column grid.
 *
 * Ported from the LifeScribe original's StyleCard — drag handle, position
 * circle, "Most Popular" ribbon, category pill, active switch, expand-to-prompts
 * — with these differences:
 *
 *   • NO "N uses" line. LifeScribe's usage counters were always 0 and the number
 *     it printed came from nowhere; a screen may only show numbers the API
 *     actually returns.
 *   • NO per-row model override dropdown. Quill's model control plane is the
 *     Pipeline Models page; a second selector here would be a rival source of
 *     truth (see the quiet link on AIStylesPage).
 *   • A withheld row shows its `unavailable_reason` verbatim, and a row with no
 *     `ls_style_key` says so — that is exactly what the backend's HONESTY_GATE
 *     will refuse to activate, so the admin can see it before clicking.
 *   • Keyboard/AT-reachable "Move up"/"Move down" buttons alongside the drag
 *     handle: pointer dragging is not the only way to reorder.
 */

import React from 'react';
import { Box, Typography, Switch, IconButton, Chip, Collapse, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatDistanceToNow } from 'date-fns';
import { colors, typography } from '../../../theme/designTokens';
import { CATEGORY_PILL_COLORS } from './constants';
import PromptExpandSection from './PromptExpandSection';
import { StyleFamily, StyleRow } from './types';

export interface StyleCardProps {
  row: StyleRow;
  rowKeyValue: string;
  position: number;
  inGrid: boolean;
  isMostPopular: boolean;
  isExpanded: boolean;
  family: StyleFamily;
  pillLabel: string;
  busy: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onToggle: (row: StyleRow, nextActive: boolean) => void;
  onEdit: (row: StyleRow) => void;
  onDelete: (row: StyleRow) => void;
  onExpandToggle: (key: string) => void;
  onMove: (key: string, direction: -1 | 1) => void;
  onCopy: (text: string, label: string) => void;
}

/** The original's timestamp: date-fns `formatDistanceToNow`, suffixed. */
function relativeTime(iso?: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  return formatDistanceToNow(then, { addSuffix: true });
}

const StyleCard: React.FC<StyleCardProps> = ({
  row,
  rowKeyValue,
  position,
  inGrid,
  isMostPopular,
  isExpanded,
  family,
  pillLabel,
  busy,
  canMoveUp,
  canMoveDown,
  onToggle,
  onEdit,
  onDelete,
  onExpandToggle,
  onMove,
  onCopy,
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id: rowKeyValue });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const pillColor = CATEGORY_PILL_COLORS[pillLabel] || { bg: '#6B7280', color: '#fff' };
  const withheld = Boolean(row.unavailable_reason);
  const missingLsKey = !row.ls_style_key;
  const updated = relativeTime(row.updated_at);

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-testid={`style-card-${rowKeyValue}`}
      sx={{
        bgcolor: inGrid ? '#fff' : '#f5f0e8',
        borderRadius: '10px',
        border: isOver ? '2px dashed #1A5F7A' : inGrid ? '1px solid #e2e8f0' : '1px solid #d6cfc2',
        borderLeft: inGrid ? `3px solid ${colors.deepTeal[500]}` : '3px solid #ccc',
        opacity: isDragging ? 0.28 : row.is_active ? 1 : 0.48,
        boxShadow: isDragging
          ? '0 8px 24px rgba(0,0,0,0.15)'
          : isOver
            ? '0 0 0 2px rgba(26,95,122,0.2)'
            : '0 1px 3px rgba(0,0,0,0.06)',
        transform: isDragging ? 'scale(0.96)' : undefined,
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 150ms ease, opacity 150ms ease, border-color 150ms ease',
      }}
    >
      {isMostPopular && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bgcolor: colors.warmGold[500],
            color: '#fff',
            px: 1,
            py: 0.25,
            fontSize: '0.62rem',
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 700,
            borderRadius: '0 9px 0 6px',
            display: 'flex',
            alignItems: 'center',
            gap: 0.3,
            zIndex: 2,
          }}
        >
          <StarIcon sx={{ fontSize: 10 }} />
          Most Popular
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 1.5, gap: 1.2 }}>
        {/* Drag handle + keyboard reorder */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.3 }}>
          <Box
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            aria-label={`Drag to reorder ${row.display_name}`}
            sx={{
              cursor: 'grab',
              color: '#94a3b8',
              userSelect: 'none',
              lineHeight: 1,
              '&:hover': { color: '#64748b' },
            }}
          >
            <Typography sx={{ fontSize: '1rem', lineHeight: 1, letterSpacing: '-0.05em' }}>
              &#x28FF;
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label={`Move ${row.display_name} up`}
            disabled={!canMoveUp}
            onClick={() => onMove(rowKeyValue, -1)}
            sx={{ p: 0.1 }}
          >
            <ArrowUpwardIcon sx={{ fontSize: 13 }} />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Move ${row.display_name} down`}
            disabled={!canMoveDown}
            onClick={() => onMove(rowKeyValue, 1)}
            sx={{ p: 0.1 }}
          >
            <ArrowDownwardIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Box>

        {/* Position circle */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: inGrid ? colors.deepTeal[500] : '#d1d5db',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 700,
            fontSize: '0.72rem',
            flexShrink: 0,
            mt: 0.2,
          }}
        >
          {position}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: '#1e293b',
              lineHeight: 1.3,
              mb: 0.3,
            }}
          >
            {row.display_name}
          </Typography>
          <Typography
            sx={{
              fontFamily: typography.fonts.mono,
              fontSize: '0.65rem',
              color: '#94a3b8',
              mb: 0.4,
            }}
          >
            {rowKeyValue}
          </Typography>
          {row.description && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#64748b',
                display: '-webkit-box',
                WebkitLineClamp: isExpanded ? 'unset' : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
                mb: 1,
              }}
            >
              {row.description}
            </Typography>
          )}

          {/* Truthful state banners — admins see why a style can't be offered. */}
          {withheld && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.6,
                bgcolor: '#FFF7ED',
                border: '1px solid #FED7AA',
                borderRadius: '6px',
                px: 1,
                py: 0.6,
                mb: 0.8,
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 14, color: '#c2410c', mt: '1px' }} />
              <Typography sx={{ fontSize: '0.7rem', color: '#9a3412', lineHeight: 1.4 }}>
                Withheld — {row.unavailable_reason}
              </Typography>
            </Box>
          )}
          {missingLsKey && !withheld && (
            <Typography sx={{ fontSize: '0.7rem', color: '#9a3412', mb: 0.8, lineHeight: 1.4 }}>
              No LifeScribe style key — activation will be refused until one is set.
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={pillLabel}
              sx={{
                height: 20,
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 700,
                fontSize: '0.6rem',
                letterSpacing: '0.04em',
                bgcolor: pillColor.bg,
                color: pillColor.color,
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
            {row.is_premium && (
              <Chip
                size="small"
                label="PREMIUM"
                sx={{
                  height: 20,
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 700,
                  fontSize: '0.6rem',
                  bgcolor: colors.warmGold[500],
                  color: '#fff',
                  '& .MuiChip-label': { px: 0.8 },
                }}
              />
            )}
            {row.source && (
              <Chip
                size="small"
                variant="outlined"
                label={row.source}
                sx={{ height: 20, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.8 } }}
              />
            )}
            <Tooltip title={row.is_active ? 'Deactivate' : 'Activate'}>
              <span>
                <Switch
                  checked={Boolean(row.is_active)}
                  disabled={busy}
                  onChange={(_e, checked) => onToggle(row, checked)}
                  size="small"
                  slotProps={{ input: { 'aria-label': `Active for ${rowKeyValue}` } }}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: colors.deepTeal[500] } }}
                />
              </span>
            </Tooltip>
            {updated && (
              <Typography
                sx={{ fontSize: '0.65rem', color: '#94a3b8', fontFamily: '"Montserrat", sans-serif' }}
              >
                {updated}
              </Typography>
            )}

            <Box sx={{ flex: 1 }} />

            <IconButton
              size="small"
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} prompts for ${row.display_name}`}
              onClick={() => onExpandToggle(rowKeyValue)}
              sx={{ p: 0.3 }}
            >
              {isExpanded ? (
                <KeyboardArrowUp sx={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowDown sx={{ fontSize: 18 }} />
              )}
            </IconButton>
            <IconButton
              size="small"
              aria-label={`Edit ${row.display_name}`}
              onClick={() => onEdit(row)}
              sx={{ p: 0.3, color: colors.deepTeal[500] }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              aria-label={`Delete ${row.display_name}`}
              onClick={() => onDelete(row)}
              sx={{ p: 0.3, color: '#ef4444' }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2, pb: 2, borderTop: '1px solid #e2e8f0' }}>
          {row.display_description && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                Shown to members
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
                {row.display_description}
              </Typography>
            </Box>
          )}
          <Box sx={{ mt: 1.5 }}>
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              LifeScribe style key
            </Typography>
            <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: '0.78rem', color: '#475569' }}>
              {row.ls_style_key || '— none —'}
            </Typography>
          </Box>
          <PromptExpandSection row={row} family={family} onCopy={onCopy} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default StyleCard;
